import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/store';
import { patientsApiSlice } from '@/store/slices/api';
import { billingApiSlice } from '@/store/slices/api/billingApiSlice';
import { reportsApiSlice } from '@/store/slices/api/reportsApiSlice';
import { API_ENDPOINTS, API_SLICE, BASE_URL } from '@/utils/constants/apiEndpoints';

import { Patient, UpdatePatientDto } from '@/types/patient';
import { LastBillableDatesResponse, PatientDetailsResponse } from '@/types/reportsPatient';

import { transmissionReportApiSlice } from './transmissionReportsApiSlice';

export const reportsPatientApiSlice = createApi({
  reducerPath: 'reportsPatientApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: ['ReportPatient'],
  endpoints: (builder) => ({
    getReportPatientDetails: builder.query<PatientDetailsResponse, string>({
      query: (patientId) => ({
        url: `/patients/v2/${patientId}`,
        method: 'GET',
        cache: 'no-store'
      }),
      keepUnusedDataFor: 1,
      providesTags: (_result, _error, patientId) => [{ type: 'ReportPatient', id: patientId }]
    }),
    getLastBillableDates: builder.query<
      LastBillableDatesResponse,
      { patientId: string; transmissionReportId: number }
    >({
      query: ({ patientId, transmissionReportId }) => ({
        url: `/patients/v2/${patientId}/last-billable-dates`,
        method: 'GET',
        params: { transmissionReportId }
      }),
      keepUnusedDataFor: 1,
      providesTags: (_result, _error, { patientId }) => [{ type: 'ReportPatient', id: patientId }]
    }),

    updatePatient: builder.mutation<{ patient: Patient }, UpdatePatientDto>({
      query: ({ patient }) => {
        const body: any = {
          referringProvider: patient?.referringProvider?.NPI,
          implantingEp: patient?.implantingEp?.NPI,
          implantingEpFreeText: patient?.implantingEpFreeText,
          referringPhysician: patient?.referringPhysician,
          oac: patient?.oac,
          practitionerId: patient?.practitioner?.id,
          diagnosisOption: patient?.diagnosisOption,
          monitorReason: patient?.monitorReason,
          birthDate: patient?.birthDate,
          sex: patient?.sex,
          mrn2: patient?.mrn2,
          inClinicPractitionerId: patient?.inClinicPractitioner?.id,
          status: patient?.status,
          statusReason: patient?.statusReason,
          statusStartDate: patient?.statusStartDate,
          statusEndDate: patient?.statusEndDate,
          statusOtherReason: patient?.statusOtherReason,
          ...(typeof patient?.isRemoteMonitored === 'boolean'
            ? { isRemoteMonitored: patient.isRemoteMonitored }
            : {}),
          ...(typeof patient?.isRhythmMonitored === 'boolean'
            ? { isRhythmMonitored: patient.isRhythmMonitored }
            : {}),
          // Only when the caller sets it (CPE checklist). Not declared in synergy's
          // UpdatePatientParams; see docs/CPE_ENROLLMENT_CHECKLIST.md.
          ...(patient && Object.prototype.hasOwnProperty.call(patient, 'departmentId')
            ? { departmentId: patient.departmentId ?? null }
            : {}),
          ...(patient && Object.prototype.hasOwnProperty.call(patient, 'financialPayerOverride')
            ? { financialPayerOverride: patient.financialPayerOverride ?? null }
            : {})
        };

        // Add nested settings if present
        if (patient?.settings) {
          body.settings = patient.settings;
        }

        return {
          url: `${API_ENDPOINTS.PATIENTS.LIST}/${patient.id}`,
          method: 'PATCH',
          body
        };
      },
      async onQueryStarted({ patient, activeReportId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          if (patient?.id) {
            dispatch(
              reportsPatientApiSlice.util.invalidateTags([
                { type: 'ReportPatient', id: patient.id }
              ])
            );
            dispatch(
              patientsApiSlice.util.invalidateTags([
                { type: API_SLICE.Patient, id: patient.id },
                { type: API_SLICE.Patient, id: `${patient.id}-diagnoses` }
              ])
            );
          }

          if (activeReportId) {
            dispatch(
              transmissionReportApiSlice.util.invalidateTags([
                { type: API_SLICE.TransmissionReport, id: activeReportId }
              ])
            );
            dispatch(
              billingApiSlice.util.invalidateTags([{ type: API_SLICE.Billing, id: activeReportId }])
            );
            dispatch(
              reportsApiSlice.util.invalidateTags([
                {
                  type: API_SLICE.TransmissionReport,
                  id: activeReportId ?? 0
                }
              ])
            );
          }
        } catch (error) {
          console.error('Error in Update Patient', error);
        }
      }
    })
  })
});

export const {
  useGetReportPatientDetailsQuery,
  useGetLastBillableDatesQuery,
  useUpdatePatientMutation
} = reportsPatientApiSlice;
